/**
 * Loading Showcase Page
 * 
 * Demonstrates all variations and use cases of the Loading component
 * - Different animation types (spinner, pulse, skeleton)
 * - All sizes (sm, md, lg)
 * - Inline and full-page modes
 * - With custom messages
 * 
 * @page
 */

import { FC, useState } from 'react';
import StandardPage from '@components/Common/StandardPage';
import { Loading } from '@components/Common/Loading';
import Button from '@components/Common/Button';
import styles from './LoadingShowcasePage.module.css';

const LoadingShowcasePage: FC = () => {
  const [fullPageLoading, setFullPageLoading] = useState(false);

  const handleToggleFullPage = () => {
    setFullPageLoading(true);
    setTimeout(() => setFullPageLoading(false), 3000);
  };

  return (
    <StandardPage
      header={{
        title: 'Loading Component Showcase',
        subtitle: 'Explore all variations and use cases of the Loading component',
      }}
    >
      {fullPageLoading && (
        <Loading
          fullPage
          backdrop
          message="Carregando página completa..."
          type="spinner"
          size="lg"
        />
      )}

      <div className={styles.container}>
        {/* Section: Animation Types */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>🎬 Tipos de Animação</h2>
          <p className={styles.sectionDescription}>
            Diferentes estilos de animação para diferentes contextos
          </p>

          <div className={styles.grid}>
            {/* Spinner */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Spinner</h3>
              <p className={styles.cardDescription}>Rotação contínua para operações ativas</p>
              <div className={styles.preview}>
                <Loading type="spinner" size="md" message="Carregando..." />
              </div>
            </div>

            {/* Pulse */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Pulse</h3>
              <p className={styles.cardDescription}>Pulsação suave para processamento</p>
              <div className={styles.preview}>
                <Loading type="pulse" size="md" message="Processando..." />
              </div>
            </div>

            {/* Skeleton */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Skeleton</h3>
              <p className={styles.cardDescription}>Efeito shimmer para placeholders</p>
              <div className={styles.preview}>
                <Loading type="skeleton" size="md" message="Carregando conteúdo..." />
              </div>
            </div>
          </div>
        </section>

        {/* Section: Sizes */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>📏 Tamanhos</h2>
          <p className={styles.sectionDescription}>
            Três tamanhos disponíveis para diferentes contextos
          </p>

          <div className={styles.grid}>
            {/* Small */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Pequeno (sm)</h3>
              <p className={styles.cardDescription}>Para elementos compactos</p>
              <div className={styles.preview}>
                <Loading type="spinner" size="sm" message="Carregando..." />
              </div>
            </div>

            {/* Medium */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Médio (md)</h3>
              <p className={styles.cardDescription}>Tamanho padrão e recomendado</p>
              <div className={styles.preview}>
                <Loading type="spinner" size="md" message="Carregando..." />
              </div>
            </div>

            {/* Large */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Grande (lg)</h3>
              <p className={styles.cardDescription}>Para destaque e visibilidade</p>
              <div className={styles.preview}>
                <Loading type="spinner" size="lg" message="Carregando..." />
              </div>
            </div>
          </div>
        </section>

        {/* Section: Variations */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>🎨 Variações</h2>
          <p className={styles.sectionDescription}>
            Combinações de tipos, tamanhos e mensagens
          </p>

          <div className={styles.grid}>
            {/* Without Message */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Sem Mensagem</h3>
              <p className={styles.cardDescription}>Loading minimalista</p>
              <div className={styles.preview}>
                <Loading type="spinner" size="md" />
              </div>
            </div>

            {/* Pulse Large with Message */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Pulse Grande</h3>
              <p className={styles.cardDescription}>Pulse com tamanho lg</p>
              <div className={styles.preview}>
                <Loading type="pulse" size="lg" message="Sincronizando..." />
              </div>
            </div>

            {/* Skeleton Small */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Skeleton Pequeno</h3>
              <p className={styles.cardDescription}>Para itens em lista</p>
              <div className={styles.preview}>
                <Loading type="skeleton" size="sm" />
              </div>
            </div>
          </div>
        </section>

        {/* Section: Use Cases */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>💡 Casos de Uso</h2>
          <p className={styles.sectionDescription}>
            Exemplos de uso prático em diferentes contextos
          </p>

          <div className={styles.grid}>
            {/* In Data Table */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Em Tabela de Dados</h3>
              <p className={styles.cardDescription}>Loading durante carregamento de dados</p>
              <div className={styles.preview} style={{ minHeight: '200px' }}>
                <Loading type="skeleton" size="md" message="Carregando tabela..." />
              </div>
            </div>

            {/* In Form Submission */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Envio de Formulário</h3>
              <p className={styles.cardDescription}>Durante processamento de dados</p>
              <div className={styles.preview}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Loading type="spinner" size="md" message="Salvando..." />
                  <Button variant="secondary" size="sm" disabled>
                    Enviar (Desabilitado)
                  </Button>
                </div>
              </div>
            </div>

            {/* Full Page Overlay */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Overlay de Página</h3>
              <p className={styles.cardDescription}>Loading em tela cheia com backdrop</p>
              <div className={styles.preview}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleToggleFullPage}
                  fullWidth
                >
                  Ativar Loading Full Page (3s)
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Section: Code Examples */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>📝 Exemplos de Código</h2>
          <p className={styles.sectionDescription}>
            Como integrar o Loading component em seu código
          </p>

          <div className={styles.codeExamples}>
            {/* Example 1 */}
            <div className={styles.codeBlock}>
              <h4>Uso Básico</h4>
              <pre>
                <code>
{`import { Loading } from '@components/Common/Loading';

// Spinner padrão com mensagem
<Loading message="Carregando dados..." />`}
                </code>
              </pre>
            </div>

            {/* Example 2 */}
            <div className={styles.codeBlock}>
              <h4>Com Hook useLoading</h4>
              <pre>
                <code>
{`import { useLoading } from '@hooks/useLoading';
import { Loading } from '@components/Common/Loading';

export const MyComponent = () => {
  const loading = useLoading();

  const handleFetch = async () => {
    loading.start('Carregando...', false);
    try {
      const data = await fetch('/api/data');
      // Process data
    } finally {
      loading.stop();
    }
  };

  return (
    <>
      {loading.isLoading && (
        <Loading message={loading.message} />
      )}
      <button onClick={handleFetch}>
        Carregar
      </button>
    </>
  );
};`}
                </code>
              </pre>
            </div>

            {/* Example 3 */}
            <div className={styles.codeBlock}>
              <h4>Full Page Overlay</h4>
              <pre>
                <code>
{`// Full page loading overlay
<Loading
  fullPage
  backdrop
  message="Processando..."
  type="spinner"
  size="lg"
/>`}
                </code>
              </pre>
            </div>

            {/* Example 4 */}
            <div className={styles.codeBlock}>
              <h4>Em DataTable</h4>
              <pre>
                <code>
{`import { DataTable } from '@components/DataTable';
import { userColumns } from './userColumns';

export const UsersPage = () => {
  const { data, isLoading } = useQuery(
    ['users'],
    () => userService.getAll()
  );

  return (
    <DataTable
      data={data || []}
      columns={userColumns}
      isLoading={isLoading}
    />
  );
};`}
                </code>
              </pre>
            </div>
          </div>
        </section>

        {/* Section: Props */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>⚙️ Propriedades</h2>
          <p className={styles.sectionDescription}>Todas as propriedades disponíveis</p>

          <div className={styles.propsTable}>
            <table>
              <thead>
                <tr>
                  <th>Propriedade</th>
                  <th>Tipo</th>
                  <th>Padrão</th>
                  <th>Descrição</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>size</code>
                  </td>
                  <td>
                    <code>'sm' | 'md' | 'lg'</code>
                  </td>
                  <td>
                    <code>'md'</code>
                  </td>
                  <td>Tamanho do indicador de loading</td>
                </tr>
                <tr>
                  <td>
                    <code>type</code>
                  </td>
                  <td>
                    <code>'spinner' | 'pulse' | 'skeleton'</code>
                  </td>
                  <td>
                    <code>'spinner'</code>
                  </td>
                  <td>Tipo de animação</td>
                </tr>
                <tr>
                  <td>
                    <code>message</code>
                  </td>
                  <td>
                    <code>string</code>
                  </td>
                  <td>
                    <code>undefined</code>
                  </td>
                  <td>Mensagem exibida abaixo do indicador</td>
                </tr>
                <tr>
                  <td>
                    <code>fullPage</code>
                  </td>
                  <td>
                    <code>boolean</code>
                  </td>
                  <td>
                    <code>false</code>
                  </td>
                  <td>Exibir como overlay em tela cheia</td>
                </tr>
                <tr>
                  <td>
                    <code>backdrop</code>
                  </td>
                  <td>
                    <code>boolean</code>
                  </td>
                  <td>
                    <code>true</code>
                  </td>
                  <td>Mostrar background escuro (fullPage apenas)</td>
                </tr>
                <tr>
                  <td>
                    <code>className</code>
                  </td>
                  <td>
                    <code>string</code>
                  </td>
                  <td>
                    <code>undefined</code>
                  </td>
                  <td>Classes CSS customizadas</td>
                </tr>
                <tr>
                  <td>
                    <code>ariaLabel</code>
                  </td>
                  <td>
                    <code>string</code>
                  </td>
                  <td>
                    <code>'Carregando...'</code>
                  </td>
                  <td>Label de acessibilidade</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section: Best Practices */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>✨ Melhores Práticas</h2>
          <p className={styles.sectionDescription}>Recomendações para usar o Loading component</p>

          <div className={styles.bestPractices}>
            <div className={styles.practiceItem}>
              <h4>✅ Use o tamanho apropriado</h4>
              <p>Use <code>size="sm"</code> em elementos pequenos, <code>size="lg"</code> para destaques</p>
            </div>

            <div className={styles.practiceItem}>
              <h4>✅ Sempre forneça contexto</h4>
              <p>Use a propriedade <code>message</code> para informar ao usuário o que está sendo carregado</p>
            </div>

            <div className={styles.practiceItem}>
              <h4>✅ Use full-page com moderação</h4>
              <p>Full page loading deve ser usado apenas para operações críticas que bloqueiam a interface</p>
            </div>

            <div className={styles.practiceItem}>
              <h4>✅ Escolha o tipo de animação</h4>
              <p><code>spinner</code> para ações ativas, <code>pulse</code> para processamento, <code>skeleton</code> para placeholders</p>
            </div>

            <div className={styles.practiceItem}>
              <h4>✅ Suporte a temas</h4>
              <p>O Loading component suporta todos os temas do portal automaticamente</p>
            </div>

            <div className={styles.practiceItem}>
              <h4>✅ Acessibilidade</h4>
              <p>O componente inclui labels ARIA apropriados e suporta <code>prefers-reduced-motion</code></p>
            </div>
          </div>
        </section>
      </div>
    </StandardPage>
  );
};

export default LoadingShowcasePage;
